/**
 * GeoTrace UI kit behaviour (pairs with css/ui.css). Loaded on every page.
 *
 * Declarative hooks — no page code needed for the common cases:
 *
 *  Sheets (native <dialog class="ui-sheet" id="x">)
 *    [data-sheet-open="x"]          open dialog #x (showModal)
 *      data-sheet-action="/url"     …and set the action of the form inside it
 *      data-sheet-fill='{"name":"v"}'  …and fill its fields by name (checkbox: bool,
 *                                   radio: matching value); elements inside the
 *                                   sheet with [data-fill-text="key"] get the value
 *                                   as text, [data-fill-show="key"] are shown only
 *                                   when the value is truthy
 *      data-sheet-reset             …and reset its form first (for "Add" sheets)
 *    [data-sheet-close]             close the enclosing sheet
 *    Clicking the backdrop or pressing Esc closes it too.
 *    The dialog receives a `ui:sheet-open` event ({ detail: { trigger, data } }).
 *
 *  Forms
 *    form[data-ui-async]            submit with fetch (urlencoded), expect JSON
 *                                   { success, message, forceReload }; shows a
 *                                   toast, closes its sheet on success, fires
 *                                   `ui:success` / `ui:error` on the form
 *      data-reload                  …reload the page after a successful save
 *    [data-confirm="Sure?"]         on a form or submit button: ask first
 *
 *  Menus (<details class="ui-menu">)
 *    Only one open at a time; closes on outside click / Esc; opens upwards
 *    when there isn't room below.
 *
 *  Filtering
 *    [data-ui-filter="#list"]       container of controls that filter #list:
 *      input[data-filter-search]    free text, matched against each item's
 *                                   data-search (or its text)
 *      select[data-filter-key="k"]  matches each item's data-k ("" = all)
 *    #list [data-filter-item]       the filterable items
 *    [data-filter-count]            receives the visible count
 *    [data-filter-empty]            shown when nothing matches
 *
 *  Range sliders
 *    input[type=range] in a page/sheet gets a thumb-aligned track fill;
 *    after setting .value from code, call ui.syncRange(input)
 *
 *  Maps
 *    ui.cooperativeMap(leafletMap)  one finger / wheel scroll the page,
 *                                   two fingers / Ctrl+wheel move the map
 *
 *  window.ui = { toast, openSheet, closeSheet, postForm, cooperativeMap, syncRange }
 */
(function () {
  // ─── Toasts ────────────────────────────────────────────────────────────────
  let toastHost = null;
  const TOAST_ICONS = {
    success: 'fa-circle-check',
    error: 'fa-circle-exclamation',
    warning: 'fa-triangle-exclamation',
    info: 'fa-circle-info',
  };

  const toast = (message, type = 'success', timeout = 3200) => {
    if (!message) return;
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'ui-toasts';
      toastHost.setAttribute('role', 'status');
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    const el = document.createElement('div');
    el.className = `ui-toast ui-toast--${type}`;
    const icon = document.createElement('i');
    icon.className = `fa-solid ${TOAST_ICONS[type] || TOAST_ICONS.success}`;
    icon.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.textContent = message;
    el.append(icon, text);
    toastHost.appendChild(el);
    setTimeout(() => {
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 220);
    }, timeout);
  };

  // ─── Sheets ────────────────────────────────────────────────────────────────
  const fillForm = (sheet, data) => {
    const form = sheet.querySelector('form');
    Object.entries(data).forEach(([name, value]) => {
      if (form) {
        const fields = form.querySelectorAll(`[name="${CSS.escape(name)}"]`);
        fields.forEach((field) => {
          if (field.type === 'checkbox') field.checked = value === true || value === 'true' || value === 1 || value === '1';
          else if (field.type === 'radio') field.checked = String(field.value) === String(value);
          else if (field.tagName === 'SELECT' && field.multiple && Array.isArray(value)) {
            [...field.options].forEach((opt) => (opt.selected = value.map(String).includes(opt.value)));
          } else field.value = value ?? '';
        });
      }
      sheet.querySelectorAll(`[data-fill-text="${CSS.escape(name)}"]`).forEach((el) => {
        el.textContent = value ?? '';
      });
      sheet.querySelectorAll(`[data-fill-show="${CSS.escape(name)}"]`).forEach((el) => {
        el.hidden = !value;
      });
    });
  };

  const openSheet = (id, { trigger = null, action = null, data = null, reset = false } = {}) => {
    const sheet = typeof id === 'string' ? document.getElementById(id) : id;
    if (!sheet) return null;
    const form = sheet.querySelector('form');
    if (reset && form) form.reset();
    if (action && form) form.action = action;
    if (data) fillForm(sheet, data);
    if (!sheet.open) sheet.showModal();
    sheet.dispatchEvent(new CustomEvent('ui:sheet-open', { detail: { trigger, data } }));
    return sheet;
  };

  const closeSheet = (sheetOrChild) => {
    const sheet = sheetOrChild?.closest ? sheetOrChild.closest('dialog') : sheetOrChild;
    if (sheet?.open) sheet.close();
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-sheet-open]');
    if (opener) {
      e.preventDefault();
      let data = null;
      if (opener.dataset.sheetFill) {
        try {
          data = JSON.parse(opener.dataset.sheetFill);
        } catch (err) {
          console.error('Bad data-sheet-fill JSON', err);
        }
      }
      openSheet(opener.dataset.sheetOpen, {
        trigger: opener,
        action: opener.dataset.sheetAction || null,
        data,
        reset: opener.hasAttribute('data-sheet-reset'),
      });
      opener.closest('details.ui-menu')?.removeAttribute('open');
      return;
    }

    const closer = e.target.closest('[data-sheet-close]');
    if (closer) {
      e.preventDefault();
      closeSheet(closer);
      return;
    }

    // Backdrop click: the event target is the <dialog> itself
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('ui-sheet')) {
      const rect = e.target.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) e.target.close();
    }
  });

  // ─── Menus ─────────────────────────────────────────────────────────────────
  const closeMenus = (except) => {
    document.querySelectorAll('details.ui-menu[open]').forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
    });
  };

  document.addEventListener(
    'toggle',
    (e) => {
      const menu = e.target;
      if (!(menu instanceof HTMLDetailsElement) || !menu.classList.contains('ui-menu') || !menu.open) return;
      closeMenus(menu);
      const list = menu.querySelector('.ui-menu__list');
      menu.classList.remove('is-up');
      if (list) {
        const rect = list.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 8 && rect.height < menu.getBoundingClientRect().top) {
          menu.classList.add('is-up');
        }
      }
    },
    true,
  );

  document.addEventListener('click', (e) => {
    if (!e.target.closest('details.ui-menu')) closeMenus(null);
    else if (e.target.closest('.ui-menu__item')) {
      // Let the item's own action run, then tidy up
      setTimeout(() => e.target.closest('details.ui-menu')?.removeAttribute('open'), 0);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenus(null);
  });

  // ─── Forms ─────────────────────────────────────────────────────────────────
  const postForm = async (form, extra = {}) => {
    const body = new URLSearchParams(new FormData(form));
    Object.entries(extra).forEach(([k, v]) => body.set(k, v));
    const res = await fetch(form.action, {
      method: (form.getAttribute('method') || 'POST').toUpperCase(),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body,
    });
    return res.json();
  };

  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const confirmText = e.submitter?.dataset.confirm || form.dataset.confirm;
      if (confirmText && !window.confirm(confirmText)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      if (!form.hasAttribute('data-ui-async')) return;
      e.preventDefault();

      const buttons = form.querySelectorAll('button, input[type="submit"]');
      const submitters = form.id ? document.querySelectorAll(`[form="${CSS.escape(form.id)}"]`) : [];
      const all = [...buttons, ...submitters];
      all.forEach((b) => (b.disabled = true));
      try {
        const data = await postForm(form);
        if (data.success) {
          toast(data.message || 'Saved');
          form.dispatchEvent(new CustomEvent('ui:success', { detail: data }));
          closeSheet(form);
          if (data.forceReload || form.hasAttribute('data-reload')) setTimeout(() => window.location.reload(), 600);
        } else {
          toast(data.message || 'Something went wrong', 'error', 5000);
          form.dispatchEvent(new CustomEvent('ui:error', { detail: data }));
        }
      } catch (err) {
        console.error(err);
        toast('Network error — please try again', 'error', 5000);
        form.dispatchEvent(new CustomEvent('ui:error', { detail: { success: false } }));
      } finally {
        all.forEach((b) => (b.disabled = false));
      }
    },
    true,
  );

  // ─── Filtering ─────────────────────────────────────────────────────────────
  const setupFilter = (container) => {
    const list = document.querySelector(container.dataset.uiFilter);
    if (!list) return;
    const search = container.querySelector('[data-filter-search]');
    const selects = container.querySelectorAll('[data-filter-key]');
    // One filtered list per page is the norm, so these are page-wide
    const countEls = document.querySelectorAll('[data-filter-count]');
    const empty = document.querySelector('[data-filter-empty]');

    const apply = () => {
      const q = (search?.value || '').trim().toLowerCase();
      let visible = 0;
      list.querySelectorAll('[data-filter-item]').forEach((item) => {
        const text = (item.dataset.search || item.textContent).toLowerCase();
        let show = !q || text.includes(q);
        selects.forEach((sel) => {
          const key = sel.dataset.filterKey;
          if (show && sel.value !== '' && String(item.dataset[key] ?? '') !== sel.value) show = false;
        });
        item.hidden = !show;
        if (show) visible++;
      });
      countEls.forEach((el) => (el.textContent = visible));
      if (empty) empty.hidden = visible !== 0;
      list.hidden = visible === 0;
    };

    search?.addEventListener('input', apply);
    selects.forEach((sel) => sel.addEventListener('change', apply));
    container.querySelectorAll('[data-filter-reset]').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (search) search.value = '';
        selects.forEach((sel) => (sel.value = ''));
        apply();
      }),
    );
    apply();
  };

  // ─── Server flash messages (partials/alerts.ejs) ────────────────────────────
  // Shown inside the page's [data-flash-inline] area if it has one (the
  // sign-in pages, where a message like "Invalid password" belongs with the
  // form), otherwise as toasts.
  const showFlash = () => {
    const data = document.getElementById('flash-data');
    if (!data) return;
    let flash = [];
    try {
      flash = JSON.parse(data.textContent);
    } catch (err) {
      console.error('Bad flash data', err);
    }
    const inline = document.querySelector('[data-flash-inline]');
    flash.forEach(({ type, text }) => {
      if (inline) {
        const box = document.createElement('div');
        box.className = `ui-callout${type === 'info' ? '' : ` ui-callout--${type === 'error' ? 'danger' : type}`}`;
        box.setAttribute('role', type === 'error' ? 'alert' : 'status');
        const icon = document.createElement('i');
        icon.className = `fa-solid ${TOAST_ICONS[type] || TOAST_ICONS.info}`;
        icon.setAttribute('aria-hidden', 'true');
        const span = document.createElement('span');
        span.textContent = text;
        box.append(icon, span);
        inline.appendChild(box);
      } else {
        toast(text, type, type === 'error' || type === 'warning' ? 6000 : 4000);
      }
    });
  };

  const init = () => {
    document.querySelectorAll('[data-ui-filter]').forEach(setupFilter);
    showFlash();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // ─── Range sliders ─────────────────────────────────────────────────────────
  // The track fill (css/ui.css) reads --p, the value's position from 0 to 1.
  // Kept in sync as the user drags; call ui.syncRange(el) after setting
  // .value from code (setting it doesn't fire an input event).
  const syncRange = (el) => {
    if (!(el instanceof HTMLInputElement) || el.type !== 'range') return;
    const min = Number(el.min || 0);
    const max = Number(el.max || 100);
    const p = max > min ? (Number(el.value) - min) / (max - min) : 0;
    el.style.setProperty('--p', String(Math.min(1, Math.max(0, p))));
  };
  const syncRanges = (root = document) => root.querySelectorAll('input[type="range"]').forEach(syncRange);

  document.addEventListener('input', (e) => syncRange(e.target));
  // A form reset restores values without input events
  document.addEventListener('reset', (e) => setTimeout(() => syncRanges(e.target), 0));
  document.addEventListener('ui:sheet-open', (e) => syncRanges(e.target), true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => syncRanges());
  else syncRanges();

  // ─── Maps inside sheets / scrolling pages ──────────────────────────────────
  // A Leaflet map that fills most of a sheet swallows every scroll gesture.
  // Make it cooperative instead (as embedded Google Maps do): one finger /
  // the mouse wheel scroll the page, two fingers / Ctrl+wheel move the map,
  // with a brief hint when someone tries the old gesture. Taps, clicks and
  // marker dragging are unaffected.
  const cooperativeMap = (map) => {
    const el = map.getContainer();
    if (el.dataset.cooperative) return;
    el.dataset.cooperative = 'true';

    const hint = document.createElement('div');
    hint.className = 'ui-map-hint';
    hint.setAttribute('aria-hidden', 'true');
    el.appendChild(hint);
    let hideTimer = null;
    const showHint = (text) => {
      hint.textContent = text;
      hint.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => hint.classList.remove('is-visible'), 1300);
    };

    // Wheel: scroll the page; Ctrl/⌘ + wheel (and trackpad pinch, which
    // browsers report as ctrl+wheel) zooms the map around the pointer
    map.scrollWheelZoom.disable();
    const mac = /Mac|iPhone|iPad/.test(navigator.platform);
    let lastZoom = 0;
    el.addEventListener(
      'wheel',
      (e) => {
        if (!(e.ctrlKey || e.metaKey)) {
          showHint(`Use ${mac ? '⌘' : 'Ctrl'} + scroll to zoom the map`);
          return;
        }
        e.preventDefault();
        const now = Date.now();
        if (now - lastZoom < 120) return;
        lastZoom = now;
        map.setZoomAround(map.mouseEventToContainerPoint(e), map.getZoom() + (e.deltaY < 0 ? 1 : -1));
      },
      { passive: false },
    );

    // Touch: no one-finger pan, so the browser scrolls the sheet. Leaflet's
    // pinch handler already pans with the midpoint of two fingers.
    if (L.Browser.touch) {
      map.dragging.disable();
      let startY = null;
      el.addEventListener(
        'touchstart',
        (e) => {
          startY = e.touches.length === 1 ? e.touches[0].clientY : null;
        },
        { passive: true },
      );
      el.addEventListener(
        'touchmove',
        (e) => {
          if (startY !== null && e.touches.length === 1 && Math.abs(e.touches[0].clientY - startY) > 10) {
            showHint('Use two fingers to move the map');
            startY = null;
          }
        },
        { passive: true },
      );
    }
  };

  window.ui = { toast, openSheet, closeSheet, postForm, cooperativeMap, syncRange };
})();
